<template>
    <section class="py-[80px] lg:py-[150px]" :style="{ background: backgroundColor }">
        <div class="container mx-auto px-4">
            <div class="max-w-7xl mx-auto">
                <!-- Centered Headline -->
                <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-semibold text-center mb-[30px] lg:mb-[50px] mx-auto" :class="titleColor">
                    <span v-html="renderTitle()"></span>
                </h2>

                <!-- Two Column Grid -->
                <div class="flex flex-col-reverse lg:grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    <!-- Left: Timeline -->
                    <div class="flex flex-col">
                        <!-- Timeline Items -->
                        <div v-for="(item, index) in items" :key="index" class="flex gap-[30px]">
                            <!-- Icon + Line column -->
                            <div class="flex flex-col items-center flex-shrink-0">
                                <!-- Icon Circle -->
                                <div class="w-16 h-16 rounded-full flex items-center justify-center z-10 flex-shrink-0 bg-[#006CFF]" aria-hidden="true">
                                    <img
                                        v-if="item.icon && (item.icon.startsWith('/') || item.icon.startsWith('http'))"
                                        :src="$asset(item.icon, 'public')"
                                        class="w-8 h-8"
                                        :alt="item.title"
                                    />
                                    <i v-else :class="[item.icon, 'text-white text-2xl contents']"></i>
                                </div>
                                <!-- Connector line – only between items, not after the last -->
                                <div v-if="index < items.length - 1" class="flex-1 w-[5px] bg-[#006CFF]" aria-hidden="true"></div>
                                <div v-if="index < 1" class="flex w-[2px]" :style="{ backgroundColor: lineColor }" aria-hidden="true"></div>
                            </div>

                            <!-- Content -->
                            <div :class="{ 'pb-[30px]': index < items.length - 1 }">
                                <h3 class="text-[20px] leading-[26px] font-bold font-body mb-3 pt-1" :class="index === 0 ? 'text-[#006CFF]' : itemTitleColor">
                                    {{ item.title }}
                                </h3>
                                <!-- eslint-disable-next-line vue/no-v-html -->
                                <p class="font-pro text-lg leading-[23px]" :class="itemTextColor" v-html="item.description"></p>
                            </div>
                        </div>
                        <!-- Optional CTA -->
                        <div v-if="showCta && ctaPosition === 'left' && !ctaDownloadLink" class="text-center mt-14">
                            <router-link
                                :to="ctaLink"
                                class="inline-flex items-center gap-2 text-white px-5 py-5 rounded-full font-semibold text-[20px] transition hover:opacity-90"
                                :style="{ background: `linear-gradient(to bottom right, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                            >
                                {{ ctaText }}
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </router-link>
                        </div>
                        <div v-if="showCta && ctaPosition === 'left' && ctaDownloadLink" class="text-left mt-14">
                            <a
                                v-if="!ctaPreventDefault"
                                :href="ctaLink"
                                download
                                class="flex justify-center lg:justify-normal lg:inline-flex items-center gap-2 text-white px-5 py-5 rounded-full font-semibold text-[20px] transition hover:opacity-90"
                                :style="{ background: `linear-gradient(to bottom right, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                            >
                                {{ ctaText }}
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v10m0 0l-4-4m4 4l4-4m-9 4h14" />
                                </svg>
                            </a>
                            <button
                                v-else
                                type="button"
                                class="flex justify-center lg:justify-normal lg:inline-flex w-full lg:w-fit items-center gap-2 text-white px-5 py-5 rounded-full font-semibold text-[20px] transition hover:opacity-90"
                                :style="{ background: `linear-gradient(to bottom right, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                                @click="$emit('ctaClick')"
                            >
                                {{ ctaText }}
                                <i class="fi fi-rr-arrow-right flex"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Right: Image -->
                    <div class="lg:sticky lg:top-24">
                        <div class="rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl">
                            <img v-if="image" :src="$asset(image, 'public')" :alt="imageAlt" class="w-full h-full object-cover mx-auto" />
                            <div v-else class="w-full aspect-[4/3] lg:aspect-[3/4] flex items-center justify-center bg-white/5">
                                <i class="ph ph-image text-white/20 text-[120px]" aria-hidden="true"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Optional CTA -->
                <div v-if="showCta && ctaPosition === 'center' && !ctaDownloadLink" class="text-center mt-14">
                    <router-link
                        :to="ctaLink"
                        class="inline-flex items-center gap-2 text-white px-5 py-5 rounded-full font-semibold text-[20px] transition hover:opacity-90"
                        :style="{ background: `linear-gradient(to bottom right, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                    >
                        {{ ctaText }}
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </router-link>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'radial-gradient(circle at 50% 50%, #072551 0%, #051A39 50%, #020E20 100%)'
    },
    title: {
        type: String,
        default: ''
    },
    titleColor: {
        type: String,
        default: 'text-white'
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightColor: {
        type: String,
        default: '#006CFF'
    },
    titleHighlightGradient: {
        type: Boolean,
        default: false
    },
    items: {
        type: Array,
        default: () => []
    },
    itemTitleColor: {
        type: String,
        default: 'text-white'
    },
    itemTextColor: {
        type: String,
        default: 'text-gray-300'
    },
    iconCircleColor: {
        type: String,
        default: '#072551'
    },
    lineColor: {
        type: String,
        default: 'rgba(0, 108, 255, 0.2)'
    },
    image: {
        type: String,
        default: ''
    },
    imageAlt: {
        type: String,
        default: ''
    },
    showCta: {
        type: Boolean,
        default: false
    },
    ctaPosition: {
        type: String,
        default: 'center'
    },
    ctaDownloadLink: {
        type: Boolean,
        default: false
    },
    ctaPreventDefault: {
        type: Boolean,
        default: false
    },
    ctaText: {
        type: String,
        default: ''
    },
    ctaLink: {
        type: [String, Object],
        default: '/'
    },
    buttonGradientFrom: {
        type: String,
        default: '#006CFF'
    },
    buttonGradientTo: {
        type: String,
        default: '#006CFF'
    }
});

defineEmits(['ctaClick']);

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    const highlightStyle = props.titleHighlightGradient
        ? 'background: linear-gradient(to top, #93802f, #e2c750); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;'
        : `color: ${props.titleHighlightColor}`;

    return parts
        .map((part, index) => {
            if (index === 0) return part;

            return `<span style="${highlightStyle}">${props.titleHighlight}</span>${part}`;
        })
        .join('');
};
</script>
